import { ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { Router, RouterLink, RouterOutlet } from '@angular/router';
import { FuseConfirmationService } from '@fuse/services/confirmation';
import { OrderDirection, PurchaseOrderListDto, PurchaseOrderStatus, TeamRole, UserDto } from 'app/api/models';
import { PurchaseOrderService } from 'app/api/services';
import { InventoryPagination } from 'app/views/admin/apps/ecommerce/inventory/inventory.types';
import { ProjectHelper } from 'app/views/admin/pages/projects/project/project.helper';
import { PurchaseOrderBuilderComponent } from '../../purchase-orders/purchase-order-builder/purchase-order-builder.component';
import { MatDialog } from '@angular/material/dialog';
import { PurchaseOrderUtils } from '../../purchase-orders/purchase-order-utils';
import { AsyncPipe, CommonModule, DatePipe, I18nPluralPipe } from '@angular/common';
import { SharedModule } from 'app/core/shared.module';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatOptionModule, MatRippleModule } from '@angular/material/core';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { TextFieldModule } from '@angular/cdk/text-field';
import { FuseFindByKeyPipe } from '@fuse/pipes/find-by-key';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { SharedComponentsModule } from 'app/core/components/shared-components.module';
import { MatTabsModule } from '@angular/material/tabs';
import { DLMSharedModule } from 'app/views/calibrations/shared/shared.module';
import { DLMSharedComponentsModule } from 'app/views/calibrations/shared/components/shared-components.module';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { QRCodeModule } from 'angularx-qrcode';
import { AppAuthService } from 'app/api/services/cookie-jwt-auth/app-auth.service';

@Component({
  selector: 'part-purchase-orders',
  templateUrl: './part-purchase-orders.component.html',
  standalone: true,
  imports: [
        CommonModule,
        SharedModule,
        RouterOutlet,
        MatSidenavModule,
        MatFormFieldModule,
        MatIconModule,
        MatInputModule,
        FormsModule,
        ReactiveFormsModule,
        MatButtonModule,
        RouterLink,
        AsyncPipe,
        I18nPluralPipe,
        MatTooltipModule,
        MatRippleModule,
        MatCheckboxModule,
        MatSelectModule,
        MatOptionModule,
        MatDatepickerModule,
        TextFieldModule,
        FuseFindByKeyPipe,
        DatePipe,
        MatSlideToggleModule,
        MatAutocompleteModule,
        MatPaginatorModule,
        SharedComponentsModule,
        MatTabsModule,

        CommonModule,
        DLMSharedModule,
        DLMSharedComponentsModule,

        MatProgressBarModule,
        QRCodeModule,
  ],
  styles: [
    /* language=SCSS */
    `
      .hover-star:hover, .favourited-star {
          color: gold;
      }

      .favourite-taken {
          color: lightgrey !important;
      }

      .scrollable-list {
          min-height: 86.6vh;
          max-height: none;
          overflow-y: auto; 
      }

      .project-row {
          height: 7.4vh;
          min-height: 62px;

          &:hover {
              background-color: #f1f5f9;
              cursor: pointer;
          } 
      }

      .elipsis {
          text-overflow: ellipsis;
          overflow: hidden;
      }

      .custom-mat-select {
        max-height: none;
      }

      ::ng-deep .custom-mat-select .mat-mdc-text-field-wrapper {
          border: none !important; /* Removes the border */
      }
      ::ng-deep .custom-mat-select.bg-transparent .mat-mdc-text-field-wrapper {
          background-color: transparent !important; /* Extra specific classes to override the OG !important */
      }

      ::ng-deep .custom-mat-select .mat-mdc-select-arrow-wrapper {
          display: none !important; /* Hides the dropdown arrow */
      }

      ::ng-deep .mdc-menu-surface.mat-mdc-select-panel {
          min-width: fit-content !important;
      }
    `,  
  ]
})
export class PartPurchaseOrdersComponent extends ProjectHelper implements OnInit {
  @ViewChild('paginator') private paginator: MatPaginator;
  @ViewChild(MatSort) private _sort: MatSort;

  isPageSetup: boolean = false;
  allowedToCreatePOs: boolean;

  public isLoading: boolean = false;
  public purchaseOrders: PurchaseOrderListDto[] = [];
  public searchTerm: string = null;
  public orderProperty: string = "PurchaseOrderId";
  public orderByAsc: boolean = false;
  public showArchived: boolean = false;
 
  public pagination: InventoryPagination = {
      length: 100,
      size: 10,
      page: 0,
      lastPage: 10,
      startIndex: 0,
      endIndex: 9
  };

  constructor(
      private _fuseConfirmationService: FuseConfirmationService,
      private purchaseOrderService: PurchaseOrderService,
      private cdr: ChangeDetectorRef,
      private router: Router,
      private dialog: MatDialog,
      private appAuth: AppAuthService,
  ) {
      super();
  }

  ngOnInit(): void {
      this.allowedToCreatePOs = PurchaseOrderUtils.hasWriteAccessToPurchaseOrders(this.appAuth.getUser());
      this.loadPurchaseOrders();
  }

  ngAfterViewInit() {
      setInterval(() => { // theres probably a better way to do this but it worked
          if (this.paginator && !this.isPageSetup) {
            this.paginator.page.subscribe((page) => {
              this.pagination.page = page?.pageIndex;
              this.pagination.size = page?.pageSize;
              this.searchTerm = this.searchTerm;
              this.loadPurchaseOrders();
            });

            this.isPageSetup = true;
          }
        }, 0);
  }

  async loadPurchaseOrders() {
    var request = {
        PageSize: this.pagination?.size,
        PageIndex: this.pagination?.page,
        SearchTerm: this.searchTerm,
        OrderProperty: this.orderProperty,
        Order: this.orderByAsc? OrderDirection.Ascending : OrderDirection.Descending,
        Archived: this.showArchived,
        IsSupplier: true,
    };
    
    this.purchaseOrderService.apiPurchaseOrderPaginatedListGet$Json$Response(request).subscribe(res => {
        if(!res?.body?.data) return;
        
        this.purchaseOrders = res.body.data.items;

        this.pagination.length = res.body.data.totalCount;
        this.pagination.page = res.body.data.currentPage;
        this.pagination.size = res.body.data.pageSize;

        this.cdr.detectChanges();
    });
  }
  
  search(event: any) {
      this.searchTerm = event?.target?.value;
      this.loadPurchaseOrders();
  }
  
  toggleArchive(event: any) {
    this.showArchived = event?.checked;
    this.loadPurchaseOrders();
  }

  orderPOs(property: string) {
      this.orderByAsc = property != this.orderProperty ? true : !this.orderByAsc;
      this.orderProperty = property;
      this.loadPurchaseOrders();
  }
  
  getStatusText(status: PurchaseOrderStatus) {
    return PurchaseOrderUtils.getStatusText(status);
  }

  getStatusStyle(status: PurchaseOrderStatus) {
    return PurchaseOrderUtils.getStatusStyle(status);
  }

  navigateToPurchaseOrderBuilder(purchaseOrderId: number = null, event = null) {
    event?.stopPropagation();
    this.dialog.open(PurchaseOrderBuilderComponent, {
      data: {
        purchaseOrderId: purchaseOrderId,
        isSupplier: true,
        isProduct: false,
      },
    autoFocus: false,
    }).afterClosed().subscribe(() => {
      this.loadPurchaseOrders();
    });
  }

  deletePurchaseOrder(purchaseOrderId: number, event: any){
    event.stopPropagation();
    if(!purchaseOrderId) return;

    var title = this.showArchived ? "Restore Sales Order" : "Delete Sales Order";
    var message = this.showArchived ? "restore" : "remove";

    // Open the confirmation dialog
    const confirmation = this._fuseConfirmationService.open({
      title  : title,
      message: 'Are you sure you want to ' + message + ' this Sales Order?',
      actions: {
          confirm: {
            label: this.showArchived ? "Restore" : "Delete",
          },
      },
    });

    // Subscribe to the confirmation dialog closed action
    confirmation.afterClosed().subscribe((result) =>
    {
        // If the confirm button pressed...
        if ( result === 'confirmed' )
        {
            // Archive + reload
            this.purchaseOrderService.apiPurchaseOrderToggleArchivePurchaseOrderPost$Json({ purchaseOrderId:  purchaseOrderId, isArchive: !this.showArchived }).subscribe(res => {
                if(!res.succeeded) return;
    
                this.loadPurchaseOrders();
            });
        }
    });
  }

  /**
   * Track by function for ngFor loops
   *
   * @param index
   * @param item
   */
  trackByFn(index: number, item: any): any
  {
      return item.id || index;
  }
}
